import React, { useEffect, useState } from "react";
import {
  Modal,
  Form,
  Input,
  Button,
  Divider,
  Select,
  Switch,
  Tooltip,
  Tag,
} from "antd";
import {
  DollarOutlined,
  CloseOutlined,
  SaveOutlined,
  StarOutlined,
  PlusOutlined,
  DeleteOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  AppstoreOutlined,
  SettingOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";
import "./addAndUpdateRentalPlan.css";

const { Option } = Select;

const currencies = ["INR", "USD", "EUR", "GBP"];

const DEFAULT_AMOUNT_ROW = { room: "", amount: "", currency: "INR", before_discount_amount: "" };

const RentalPlanEditModal = ({ visible, onClose, planData, onSave }) => {
  const [form] = Form.useForm();
  const [features, setFeatures] = useState([{ name: "", enabled: false }]);
  const [amounts, setAmounts] = useState([{ ...DEFAULT_AMOUNT_ROW }]);

  useEffect(() => {
    if (visible && planData) {
      if (planData.features && Array.isArray(planData.features)) {
        setFeatures(
          planData.features.map(([name, enabled]) => ({ name, enabled }))
        );
      } else {
        setFeatures([{ name: "", enabled: false }]);
      }

      if (planData.amounts && Array.isArray(planData.amounts) && planData.amounts.length > 0) {
        setAmounts(planData.amounts);
      } else {
        setAmounts([{ ...DEFAULT_AMOUNT_ROW }]);
      }

      form.setFieldsValue({ ...planData, features: undefined, amounts: undefined });
    } else if (visible) {
      form.resetFields();
      setFeatures([{ name: "", enabled: false }]);
      setAmounts([{ ...DEFAULT_AMOUNT_ROW }]);
    }
  }, [visible, planData, form]);

  const handleFinish = (values) => {
    const featuresPayload = features
      .filter((f) => f.name.trim() !== "")
      .map((f) => [f.name, f.enabled]);

    onSave({ ...values, features: featuresPayload, amounts });
  };

  // Feature helpers
  const addFeature = () => setFeatures([...features, { name: "", enabled: false }]);
  const removeFeature = (index) => setFeatures(features.filter((_, i) => i !== index));
  const updateFeature = (index, key, value) => {
    const updated = [...features];
    updated[index] = { ...updated[index], [key]: value };
    setFeatures(updated);
  };

  // Amount helpers
  const addAmountRow = () => setAmounts([...amounts, { ...DEFAULT_AMOUNT_ROW }]);
  const removeAmountRow = (index) => setAmounts(amounts.filter((_, i) => i !== index));
  const updateAmount = (index, key, value) => {
    const updated = [...amounts];
    updated[index] = { ...updated[index], [key]: value };
    setAmounts(updated);
  };

  const modalTitle = (
    <div className="rental-plan-edit-modal-header-content">
      <div className="rental-plan-edit-header-icon-wrap">
        <DollarOutlined style={{ color: "white", fontSize: 18 }} />
      </div>
      <div className="rental-plan-edit-header-text">
        <span className="rental-plan-edit-header-title">
          {planData?._id ? "Edit Plan" : "Create New Plan"}
        </span>
        <span className="rental-plan-edit-header-subtitle">
          {planData?._id ? "Update plan details below" : "Fill in the details to create a plan"}
        </span>
      </div>
    </div>
  );

  return (
    <Modal
      title={modalTitle}
      open={visible}
      onCancel={onClose}
      closeIcon={<CloseOutlined style={{ color: "white", fontSize: 16 }} />}
      footer={null}
      destroyOnClose
      width={740}
      className="rental-plan-edit-modal-override"
      styles={{
        header: {
          background: "#070C18",
          borderRadius: "14px 14px 0 0",
          padding: "20px 24px",
          borderBottom: "none",
        },
        body: {
          padding: "0",
          background: "#f4f6fb",
        },
      }}
    >
      <div className="rental-plan-edit-modal-form">
        <Form form={form} layout="vertical" onFinish={handleFinish}>

          {/* ── Section 1: Basic Information ── */}
          <div className="rpm-section">
            <div className="rpm-section-label">
              <AppstoreOutlined className="rpm-section-icon" />
              <span>Basic Information</span>
            </div>

            <div className="rental-plan-edit-grid">
              <Form.Item
                label="Plan Name"
                name="plan_name"
                rules={[{ required: true, message: "Please input plan name" }]}
              >
                <Input
                  size="large"
                  placeholder="e.g. Starter, Pro, Business"
                  className="rental-plan-edit-input-override"
                />
              </Form.Item>

              <Form.Item label="Position" name="position">
                <Input
                  type="number"
                  size="large"
                  placeholder="Display order (e.g. 1)"
                  className="rental-plan-edit-input-override"
                />
              </Form.Item>
            </div>

            <Form.Item label="Description" name="description">
              <Input.TextArea
                rows={2}
                placeholder="Short description of this plan"
                className="rental-plan-edit-input-override"
              />
            </Form.Item>

            <div className="rpm-toggle-row">
              <div className="rpm-toggle-item">
                <span className="rpm-toggle-label">Status</span>
                <Form.Item name="status" initialValue="active" noStyle>
                  <Select size="middle" className="rental-plan-edit-input-override rpm-status-select">
                    <Option value="active">
                      <Tag color="green" style={{ margin: 0 }}>Active</Tag>
                    </Option>
                    <Option value="inactive">
                      <Tag color="red" style={{ margin: 0 }}>Inactive</Tag>
                    </Option>
                  </Select>
                </Form.Item>
              </div>
              <div className="rpm-toggle-item">
                <span className="rpm-toggle-label">Mark as Most Popular</span>
                <Form.Item name="is_most_popular" valuePropName="checked" noStyle>
                  <Switch
                    checkedChildren={<StarOutlined />}
                    unCheckedChildren={<StarOutlined />}
                    className="rpm-popular-switch"
                  />
                </Form.Item>
              </div>
            </div>
          </div>

          {/* ── Section 2: Pricing ── */}
          <div className="rpm-section">
            <div className="rpm-section-label">
              <DollarOutlined className="rpm-section-icon" />
              <span>Pricing by Room Count</span>
            </div>

            <div className="rpm-amounts-header-row">
              <span className="rpm-amounts-col-head">Rooms</span>
              <span className="rpm-amounts-col-head">Amount</span>
              <span className="rpm-amounts-col-head">Currency</span>
              <span className="rpm-amounts-col-head">Before Discount</span>
              <span className="rpm-amounts-col-head" style={{ width: 36 }}></span>
            </div>

            <div className="rpm-amounts-list">
              {amounts.map((row, index) => (
                <div key={index} className="rpm-amount-row">
                  <Input
                    type="number"
                    placeholder="Rooms"
                    value={row.room}
                    onChange={(e) => updateAmount(index, "room", e.target.value)}
                    className="rental-plan-edit-input-override rpm-amount-input"
                  />
                  <Input
                    type="number"
                    placeholder="Amount"
                    value={row.amount}
                    onChange={(e) => updateAmount(index, "amount", e.target.value)}
                    className="rental-plan-edit-input-override rpm-amount-input"
                  />
                  <Select
                    value={row.currency}
                    onChange={(val) => updateAmount(index, "currency", val)}
                    className="rental-plan-edit-input-override rpm-currency-select"
                  >
                    {currencies.map((c) => (
                      <Option key={c} value={c}>{c}</Option>
                    ))}
                  </Select>
                  <Input
                    type="number"
                    placeholder="Original price"
                    value={row.before_discount_amount}
                    onChange={(e) => updateAmount(index, "before_discount_amount", e.target.value)}
                    className="rental-plan-edit-input-override rpm-amount-input"
                  />
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => removeAmountRow(index)}
                    disabled={amounts.length === 1}
                    className="rpm-delete-btn"
                  />
                </div>
              ))}
            </div>

            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onClick={addAmountRow}
              className="rental-plan-add-feature-btn rpm-add-amount-btn"
              block
            >
              Add Pricing Tier
            </Button>
          </div>

          {/* ── Section 3: Features ── */}
          <div className="rpm-section">
            <div className="rpm-section-label">
              <UnorderedListOutlined className="rpm-section-icon" />
              <span>Plan Features</span>
            </div>

            <div className="rental-plan-features-list">
              {features.map((feature, index) => (
                <div key={index} className="rental-plan-feature-row">
                  <Tooltip
                    title={feature.enabled ? "Click to mark as Not Available" : "Click to mark as Available"}
                  >
                    <div
                      className={`rental-plan-feature-availability ${feature.enabled ? "available" : "not-available"}`}
                      onClick={() => updateFeature(index, "enabled", !feature.enabled)}
                    >
                      {feature.enabled ? (
                        <><CheckCircleFilled className="rental-feature-icon" /> Available</>
                      ) : (
                        <><CloseCircleFilled className="rental-feature-icon" /> Not Available</>
                      )}
                    </div>
                  </Tooltip>
                  <Input
                    value={feature.name}
                    onChange={(e) => updateFeature(index, "name", e.target.value)}
                    placeholder="Enter feature name"
                    className="rental-plan-edit-input-override rental-plan-feature-input"
                  />
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => removeFeature(index)}
                    disabled={features.length === 1}
                    className="rental-plan-feature-delete-btn"
                  />
                </div>
              ))}

              <Button
                type="dashed"
                icon={<PlusOutlined />}
                onClick={addFeature}
                className="rental-plan-add-feature-btn"
                block
              >
                Add Feature
              </Button>
            </div>
          </div>

          {/* ── Action Buttons ── */}
          <div className="rpm-footer">
            <Button
              size="large"
              onClick={onClose}
              className="rental-plan-edit-btn rental-plan-edit-btn-cancel"
            >
              Cancel
            </Button>
            <Button
              htmlType="submit"
              size="large"
              icon={<SaveOutlined />}
              className="rental-plan-edit-btn rental-plan-edit-btn-primary rental-plan-edit-btn-override"
            >
              <span className="rental-update-btn">
                {planData?._id ? "Update Plan" : "Create Plan"}
              </span>
            </Button>
          </div>

        </Form>
      </div>
    </Modal>
  );
};

export default RentalPlanEditModal;